import { createContext } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router-dom'
import { Box, Typography } from '@mui/material'

import { EditConfigMenu } from '@components/ConfigEditMenu'
import { PageTitle } from '@components/PageTitle'
import { ExportAllCheckbox } from '@components/ui/ExportAllCheckbox'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY_CONFIG_TYPE, FLEXIBLE, VISIBLE_HIDDEN } from '@constants'

import { AllVariables } from './components/AllVariables/AllVariables'
import { CopyDialog } from './components/CopyDialog'
import { useVariables } from './hooks'
import { PageContextType } from './types'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const Variables = () => {
  const { t } = useTranslation()

  const {
    data: {
      methods,
      isAllVariables,
      isLoadingVariables,
      allVariables,
      showCopyDialog,
      copyVariableData,
      isLoadingCopyVariable,
    },
    handlers: {
      handleCreateVariable,
      handleEditVariable,
      handleShowDialog,
      handleCloseDialog,
      handleCopyVariable,
      handleDeleteVariable,
    },
  } = useVariables()

  const contextValue = {
    methods,
    variableTables: allVariables,
    variablesIsLoading: isLoadingVariables,
    onCreateVariable: handleCreateVariable,
    onEditVariable: handleEditVariable,
    onShowCopyDialog: handleShowDialog,
    onCopyVariable: handleCopyVariable,
    onDeleteVariable: handleDeleteVariable,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.VARIABLES}>
        {showCopyDialog && copyVariableData && (
          <CopyDialog
            copyData={copyVariableData}
            dialogTitle={t('copying.form.dialogTitle')}
            isShow={showCopyDialog}
            loading={isLoadingCopyVariable}
            models={allVariables.map(variable => variable.internalId)}
            onClose={handleCloseDialog}
            onCopy={handleCopyVariable}
          />
        )}
        <Outlet />
        <Box
          sx={{
            ...FLEXIBLE,
            ...(!isAllVariables && VISIBLE_HIDDEN),
            pb: 1,
          }}
        >
          <PageTitle
            end={
              <Box sx={{ py: 0.5 }}>
                <EditConfigMenu />
              </Box>
            }
            start={
              <>
                <Typography display='inline' sx={{ mr: 1, ml: 1 }} variant='h6'>
                  {t('pageTitle.models')}
                </Typography>
                <Typography display='inline' variant='subtitle2'>
                  {t('pageTitle.variables')}
                </Typography>
              </>
            }
          />
          <ExportAllCheckbox />
          <AllVariables />
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
